import type { CommissionPackageMode } from '@hue/contracts';
import type { SaleCommissionOptions, SaleLineCommissionInput } from '../../lib/api.js';
import type { CartLine } from './sale-cart.js';

export const NO_COMMISSION = 'none';

/// O que a pessoa escolheu na linha. Sem escolha, a linha segue o padrão (ADR 0082, decisão 4).
export interface LineCommission {
  shares: Array<{ resourceId: string | null; quantity: number }>;
  /// A parte que recebe a próxima leitura: a alterada por último (decisão 5).
  active: number;
  assistantResourceId: string | null;
}

export function ruleOfLine(options: SaleCommissionOptions | undefined, line: CartLine) {
  return options?.rules.find((rule) => rule.itemKind === line.kind && rule.referenceId === line.id);
}

/// O pacote por sessão não tem campo: recebe quem atender, a cada sessão (decisão 24).
export function showsCommissionField(
  options: SaleCommissionOptions | undefined,
  line: CartLine,
): boolean {
  const rule = ruleOfLine(options, line);
  const mode: CommissionPackageMode | null | undefined = rule?.packageMode;
  return Boolean(options?.enabled && rule && mode !== 'PER_SESSION');
}

export function initialCommission(
  defaultResourceId: string | null,
  quantity: number,
): LineCommission {
  return {
    shares: [{ resourceId: defaultResourceId, quantity }],
    active: 0,
    assistantResourceId: null,
  };
}

/// Mantém as partes somando a quantidade da linha. O que entra vai para a parte ativa; o que sai
/// tira da última parte para a primeira, e a parte zerada some.
export function reconcileCommission(current: LineCommission, quantity: number): LineCommission {
  const total = current.shares.reduce((sum, share) => sum + share.quantity, 0);
  if (total === quantity) return current;
  if (total < quantity)
    return {
      ...current,
      shares: current.shares.map((share, index) =>
        index === current.active
          ? { ...share, quantity: share.quantity + quantity - total }
          : share,
      ),
    };
  let excess = total - quantity;
  const shares = [...current.shares].reverse().flatMap((share) => {
    const taken = Math.min(share.quantity, excess);
    excess -= taken;
    return share.quantity - taken > 0 ? [{ ...share, quantity: share.quantity - taken }] : [];
  });
  return {
    ...current,
    shares: shares.reverse(),
    active: Math.min(current.active, shares.length - 1),
  };
}

export function choosePerson(
  current: LineCommission,
  index: number,
  resourceId: string | null,
): LineCommission {
  return {
    ...current,
    active: index,
    shares: current.shares.map((share, position) =>
      position === index ? { ...share, resourceId } : share,
    ),
    // Quem recebe a linha toda não é assistente dela, e "Sem comissão" não tem de quem tirar.
    assistantResourceId:
      resourceId === null || current.assistantResourceId === resourceId
        ? null
        : current.assistantResourceId,
  };
}

/// Abre as partes: a última parte cede uma unidade à parte nova, sem ninguém escolhido ainda.
export function splitCommission(current: LineCommission): LineCommission {
  const last = current.shares.length - 1;
  const giver = current.shares[last]!;
  if (giver.quantity < 2) return current;
  return {
    assistantResourceId: null,
    active: last + 1,
    shares: [
      ...current.shares.slice(0, last),
      { ...giver, quantity: giver.quantity - 1 },
      { resourceId: null, quantity: 1 },
    ],
  };
}

export function changeShareQuantity(
  current: LineCommission,
  index: number,
  delta: number,
): LineCommission {
  const share = current.shares[index]!;
  if (delta < 0 && share.quantity === 1)
    return {
      ...current,
      active: 0,
      shares: current.shares.filter((_, position) => position !== index),
    };
  return {
    ...current,
    active: index,
    shares: current.shares.map((one, position) =>
      position === index ? { ...one, quantity: one.quantity + delta } : one,
    ),
  };
}

export function commissionInputOf(commission: LineCommission): SaleLineCommissionInput {
  return {
    shares: commission.shares.map(({ resourceId, quantity }) => ({ resourceId, quantity })),
    ...(commission.assistantResourceId
      ? { assistantResourceId: commission.assistantResourceId }
      : {}),
  };
}

/// A frase da revisão antes de confirmar, sem valores (decisão 7).
export function commissionReview(
  commission: LineCommission,
  nameOf: (resourceId: string) => string,
): string {
  const people = commission.shares.map((share) => {
    const name = share.resourceId ? nameOf(share.resourceId) : 'Sem comissão';
    return commission.shares.length > 1 ? `${String(share.quantity)} para ${name}` : name;
  });
  const assistant = commission.assistantResourceId
    ? `, com ${nameOf(commission.assistantResourceId)} de assistente`
    : '';
  return `${people.join(', ')}${assistant}`;
}

/// Repetir o profissional em duas partes não existe: o servidor recusa, e a tela avisa antes.
export function commissionProblem(commission: LineCommission): string | null {
  const people = commission.shares.flatMap((share) => (share.resourceId ? [share.resourceId] : []));
  return new Set(people).size === people.length
    ? null
    : 'O mesmo profissional aparece em duas partes. Junte as quantidades em uma parte só.';
}
